import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import ResultStatus from "../classes/ResultStatus";
import LabelWithInputField from "./LabelWithInputField";
import SuccessMessage from "../classes/SuccessMessage";
import EventBus from "../utils/EventBus";
import PageNavigator from "../classes/PageNavigator";
import Result from "../classes/Result";
import BooleanToggleView from "./BooleanToggleView";
import PopupWrapperView from "./PopupWrapperView";
import Button from "./Button";
import DBSaveStatus from "../utils/DBSaveStatus";
import ListWrapper from "../utils/ListWrapper";
import CheckServerErrors from "./CheckServerErrors";
import TextView from "./TextView";
import Hospital from "../models/Hospital";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import HospitalStatus from "../classes/HospitalStatus";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _AddOrEditHospitalViewOnReload = () => void;

type _CancelButtonOnPressed = (d3eState: AddOrEditHospitalViewRefs) => void;

type _Button3OnPressed = (d3eState: AddOrEditHospitalViewRefs) => void;

type _LabelwithInputFieldOnChanged = (
  text: string,
  d3eState: AddOrEditHospitalViewRefs
) => void;

type _LabelwithInputField5OnChanged = (
  text: string,
  d3eState: AddOrEditHospitalViewRefs
) => void;

export interface AddOrEditHospitalViewProps extends BaseUIProps {
  key?: string;
  obj: Hospital;
  onReload?: _AddOrEditHospitalViewOnReload;
}
/// To store state data for AddOrEditHospitalView
class AddOrEditHospitalViewRefs {
  public button3: Button3State = new Button3State();
  public cancelButton: CancelButtonState = new CancelButtonState();
}

interface Button3WithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditHospitalViewRefs;
  _onSaveHandler?: _Button3OnPressed;
}

class Button3State extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _Button3WithState extends ObservableComponent<Button3WithStateProps> {
  button3FocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: Button3WithStateProps) {
    super(props);

    this.initState();
  }
  public get button3(): Button3State {
    return this.props.d3eState.button3;
  }
  public get d3eState(): AddOrEditHospitalViewRefs {
    return this.props.d3eState;
  }
  public get _onSaveHandler(): _Button3OnPressed {
    return this.props._onSaveHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("button3", null, this.button3);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["button3", "button3.", "button3.hover"], this.rebuild);
  }
  public button3OnEnter(event): void {
    return this.button3.setHover(true);
  }
  public button3OnExit(event): void {
    return this.button3.setHover(false);
  }
  public dispose(): void {
    this.button3.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 100,
      child: Button({
        padding: this.button3.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.button3.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.button3.disable,
        onPressed: () => {
          this._onSaveHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Save" }),
        onEnter: (event) => {
          this.button3OnEnter(event);
        },
        onExit: (event) => {
          this.button3OnExit(event);
        },
      }),
      className: "x13 hc",
    });
  }
}
function Button3WithState(props: Button3WithStateProps) {
  return React.createElement(_Button3WithState, props);
}

interface CancelButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditHospitalViewRefs;
  _onCancelHandler?: _CancelButtonOnPressed;
}

class CancelButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _CancelButtonWithState extends ObservableComponent<CancelButtonWithStateProps> {
  cancelButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancelButton(): CancelButtonState {
    return this.props.d3eState.cancelButton;
  }
  public get d3eState(): AddOrEditHospitalViewRefs {
    return this.props.d3eState;
  }
  public get _onCancelHandler(): _CancelButtonOnPressed {
    return this.props._onCancelHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancelButton", null, this.cancelButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["cancelButton", "cancelButton.", "cancelButton.hover"],
      this.rebuild
    );
  }
  public cancelButtonOnEnter(event): void {
    return this.cancelButton.setHover(true);
  }
  public cancelButtonOnExit(event): void {
    return this.cancelButton.setHover(false);
  }
  public dispose(): void {
    this.cancelButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      width: 100,
      child: Button({
        padding: this.cancelButton.hover
          ? cStyle.tButtonSecondaryPaddingOnHover
          : cStyle.tButtonSecondaryPaddingOn,
        decoration: this.cancelButton.hover
          ? cStyle.tButtonSecondaryDecorationOnHover
          : cStyle.tButtonSecondaryDecorationOn,
        disable: this.cancelButton.disable,
        onPressed: () => {
          this._onCancelHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Cancel" }),
        onEnter: (event) => {
          this.cancelButtonOnEnter(event);
        },
        onExit: (event) => {
          this.cancelButtonOnExit(event);
        },
      }),
      className: "x7b hc",
    });
  }
}
function CancelButtonWithState(props: CancelButtonWithStateProps) {
  return React.createElement(_CancelButtonWithState, props);
}

class _AddOrEditHospitalViewState extends ObservableComponent<AddOrEditHospitalViewProps> {
  static defaultProps = { obj: null, onReload: null };
  d3eState: AddOrEditHospitalViewRefs = new AddOrEditHospitalViewRefs();
  errors: Array<string> = ListWrapper.widget(this, "errors");
  isActive: boolean = false;
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  private _nameErrors: Array<string> = [];
  public nameUniqueError: string;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddOrEditHospitalViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): Hospital {
    return this.props.obj;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(
      ["errors", "isActive", "nameErrors", "obj", "obj.address", "obj.name"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AddOrEditHospitalViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public setErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.errors, val);

    if (!isValChanged) {
      return;
    }

    this.errors.clear();

    this.errors.addAll(val);

    this.fire("errors", this);
  }
  public addToErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errors.contains(val)) this.errors.add(val);
    } else {
      this.errors.remove(this.errors.elementAt(index));

      this.errors.add(val);
    }

    this.fire("errors", this, val, true);
  }
  public removeFromErrors(val: string): void {
    this.errors.remove(val);

    this.fire("errors", this, val, false);
  }
  public setIsActive(val: boolean): void {
    let isValChanged: boolean = this.isActive !== val;

    if (!isValChanged) {
      return;
    }

    this.isActive = val;

    this.fire("isActive", this);
  }
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public get nameErrors(): Array<string> {
    return this._nameErrors ?? [];
  }
  public set nameErrors(val: Array<string>) {
    this._nameErrors.clear();

    this._nameErrors.addAll(val);
  }
  public async computeNameErrors(): Promise<void> {
    this.nameErrors = [];

    let it: string = this.obj.name;

    if (it == null || it === "") {
      this.nameErrors.add("Name cannot be empty.");
    }

    this.fire("nameErrors", this);
  }
  public async checkNameUniqueInHospital(): Promise<void> {
    if (this.obj == null) {
      return;
    }

    let isUnique: boolean =
      await RPCServices.getUniqueChecker().checkNameUniqueInHospital(
        this.obj,
        this.obj.name
      );

    if (!isUnique) {
      this.nameUniqueError = "Duplicate name found with this value.";
    }
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    if (this.nameErrors.isNotEmpty) {
      return false;
    }

    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    this.computeNameErrors();

    errors.addAll(this.nameErrors);

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return PopupWrapperView({
      title:
        this.obj.saveStatus !== DBSaveStatus.New
          ? "Edit Hospital"
          : "Add Hospital",
      content: ui.Column({
        crossAxisAlignment: ui.CrossAxisAlignment.start,
        mainAxisAlignment: ui.MainAxisAlignment.start,
        children: [
          LabelWithInputField({
            name: "Name",
            placeHolder: "Enter Name",
            isRequired: true,
            value: this.obj.name,
            errors: this.nameErrors,
            onChanged: (text) => {
              this.labelwithInputFieldonChanged(text, this.d3eState);
            },
            className: "x8c hc h",
            key: "0",
          }),
          ui.Container({
            margin: ui.EdgeInsets.fromLTRB(0.0, 10.0, 0.0, 0.0, new Map()),
            child: LabelWithInputField({
              name: "Address",
              placeHolder: "Enter Address",
              value: this.obj.address,
              fieldHeight: 100,
              maxLines: 4,
              onChanged: (text) => {
                this.labelwithInputField5onChanged(text, this.d3eState);
              },
            }),
            key: "1",
            className: "x0b hc h",
          }),
          ui.Container({
            child: ui.Container({
              margin: ui.EdgeInsets.fromLTRB(10.0, 5.0, 0.0, 0.0, new Map()),
              child: BooleanToggleView({
                activeText: "Active",
                inActiveText: "Inactive",
                label: "Status",
                value: this.isActive,
                onChanged: (value) => {
                  this.onStatusChangeHandler(value, this.d3eState);
                },
              }),
              className: "x53 hc",
            }),
            className: "xbdc hc",
            key: "2",
          }),
        ],
        className: "x23 hc h",
      }),
      buttons: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            this.errors.isNotEmpty
              ? CheckServerErrors({ errors: this.errors })
              : [],
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                CancelButtonWithState({
                  d3eState: this.d3eState,
                  _onCancelHandler: this.onCancelHandler,
                  key: "0",
                }),
                Button3WithState({
                  d3eState: this.d3eState,
                  _onSaveHandler: this.onSaveHandler,
                  key: "1",
                }),
              ],
              key: "1",
            }),
          ],
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AddOrEditHospitalView x97 hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    //  obj.status = HospitalStatus.Active;

    //  if(obj.status == HospitalStatus.Active) {

    //      this.isActive = true;

    //  } else {

    //      this.isActive = false;

    //  }

    if (this.obj.saveStatus === DBSaveStatus.New) {
      this.obj.setStatus(HospitalStatus.Active);

      this.setIsActive(true);
    } else {
      if (this.obj.status === HospitalStatus.Active) {
        this.setIsActive(true);
      } else {
        this.setIsActive(false);
      }
    }
  };
  public onCancelHandler = (d3eState: AddOrEditHospitalViewRefs): void => {
    this.obj.restore();

    this.navigator.close();
  };
  public onStatusChangeHandler = (
    value: boolean,
    d3eState: AddOrEditHospitalViewRefs
  ): void => {
    this.setIsActive(!this.isActive);

    if (this.isActive) {
      this.obj.setStatus(HospitalStatus.Active);
    } else {
      this.obj.setStatus(HospitalStatus.Inactive);
    }
  };
  public onSaveHandler = async (
    d3eState: AddOrEditHospitalViewRefs
  ): Promise<void> => {
    let result: Result<Hospital> = await this.obj.save();

    if (result.status === ResultStatus.Success) {
      this.onReload();

      EventBus.get().fire(
        new SuccessMessage({ message: "Successfully Saved" })
      );

      this.navigator.close();
    } else {
      this.setErrors(result.errors);
    }
  };
  public get onReload(): _AddOrEditHospitalViewOnReload {
    return this.props.onReload;
  }
  public labelwithInputFieldonChanged = (
    val: string,
    d3eState: AddOrEditHospitalViewRefs
  ): void => {
    this.obj.setName(val);
  };
  public labelwithInputField5onChanged = (
    val: string,
    d3eState: AddOrEditHospitalViewRefs
  ): void => {
    this.obj.setAddress(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get button3() {
    return this.d3eState.button3;
  }
  public get cancelButton() {
    return this.d3eState.cancelButton;
  }
}
export default function AddOrEditHospitalView(
  props: AddOrEditHospitalViewProps
) {
  return React.createElement(_AddOrEditHospitalViewState, {
    ..._AddOrEditHospitalViewState.defaultProps,
    ...props,
  });
}
